/**
 * Route / bounds / polyline geometry shared by BOTH map engines — nothing here is Leaflet- or
 * Google-specific, despite the `@googlemaps/polyline-codec` import (a pure encode/decode of the
 * polyline string format, no Google runtime and no billing).
 *
 * Was `maps-google.helpers.ts`, which read as Google-SDK code and made this a false positive when
 * grepping for removal candidates.
 */
import { decode } from "@googlemaps/polyline-codec";
import { isValidCoordinate } from "geolib";

import {
  MAP_GOOGLE_TIMESTAMP_MS_LENGTH,
  MAP_LAT_LNG_TUPLE_LENGTH,
  MAP_ROUTE_POLYLINE_DECODE_CACHE_MAX_ENTRIES,
} from "../constants";
import { ILocationInfo, IRideRoute } from "../models";
import { TLatLngExpression, TLatLngTuple } from "../models/map.models";
import { buildPartsLayoutSignature } from "./layout-signature.helpers";
import { getLatLngFromRenderState } from "./live-map";
import { Log } from "./log.helpers";
import { isLocationInfoWithValidMapCoordinates } from "./map.helpers";
import { isTrackingRoute } from "./ride";

/** Decoded polyline / bounds tuples `[lat, lng]` — validated with geolib. */
export const isValidCoordinateTuple = (pos: unknown): pos is TLatLngTuple => {
  if (!pos || !Array.isArray(pos) || pos.length !== MAP_LAT_LNG_TUPLE_LENGTH) {
    return false;
  }

  const [lat, lng] = pos;

  if (typeof lat !== "number" || typeof lng !== "number") {
    return false;
  }

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return false;
  }

  return isValidCoordinate({ lat, lng });
};

export const mapBoundsExists = ({
  bounds,
  newItem,
}: {
  bounds: TLatLngTuple[];
  newItem: TLatLngTuple;
}): boolean => {
  if (!isValidCoordinateTuple(newItem)) {
    return false;
  }

  return bounds.some(
    x =>
      isValidCoordinateTuple(x) && x[0] === newItem[0] && x[1] === newItem[1],
  );
};

export const mapBoundsExistsLocationInfo = ({
  bounds,
  locationInfo,
}: {
  bounds: TLatLngTuple[];
  locationInfo: ILocationInfo;
}): boolean => {
  return mapBoundsExists({
    bounds,
    newItem: [locationInfo.latitude ?? 0, locationInfo.longitude ?? 0],
  });
};

const routePolylineDecodeCache = new Map<string, TLatLngTuple[]>();

/** Cached polyline decode for bulk live-map routes (autofit + layers). */
export const decodeRoutePolylinePositions = (
  routePolyline: string,
): TLatLngTuple[] => {
  const cached = routePolylineDecodeCache.get(routePolyline);

  if (cached) {
    return cached;
  }

  try {
    const positions = decode(routePolyline);
    const validPositions = positions?.filter(isValidCoordinateTuple) ?? [];

    if (
      routePolylineDecodeCache.size >=
      MAP_ROUTE_POLYLINE_DECODE_CACHE_MAX_ENTRIES
    ) {
      routePolylineDecodeCache.clear();
    }

    routePolylineDecodeCache.set(routePolyline, validPositions);

    return validPositions;
  } catch (error) {
    Log.error("Error decoding polyline", error);

    return [];
  }
};

const coordinateDedupKey = (lat: number, lng: number): string =>
  `${lat},${lng}`;

/**
 * Autofit signature keyed on the routes' COORDINATE GEOMETRY — every planned station coordinate plus each
 * tracking route's latest GPS point. Changes whenever the geometry moves (e.g. a car's position on a poll),
 * so the map re-fits. Ignores `routes` array reference churn. Compare with
 * `buildRoutesAutofitSelectionSignature`, which ignores coordinates entirely.
 */
export const buildRoutesAutofitGeometrySignature = (
  routes: IRideRoute[],
): string => {
  if (!routes?.length) {
    return "";
  }

  const parts: string[] = [];

  routes.forEach(route => {
    if (!route) {
      return;
    }

    parts.push(
      String(route.rideId ?? route.stationRouteId ?? route.courseId ?? ""),
    );

    if (isTrackingRoute(route)) {
      const renderState = route.trackingRenderState;

      if (renderState?.pointCount) {
        const lastIndex = renderState.pointCount - 1;
        const [lat, lng] = getLatLngFromRenderState(renderState, lastIndex);
        parts.push(`${lat},${lng}`);
        return;
      }

      const lastLocation = route.locations?.at(-1);

      if (lastLocation && isLocationInfoWithValidMapCoordinates(lastLocation)) {
        parts.push(`${lastLocation.latitude},${lastLocation.longitude}`);
      }

      return;
    }

    route.locations?.forEach(locationInfo => {
      if (!isLocationInfoWithValidMapCoordinates(locationInfo)) {
        return;
      }

      parts.push(`${locationInfo.latitude},${locationInfo.longitude}`);
    });
  });

  if (!parts.length) {
    return "";
  }

  return buildPartsLayoutSignature(parts);
};

/**
 * Autofit signature keyed on the SELECTED route SET — the ids (ride / station-route / course) only,
 * ignoring every coordinate. The live map re-fits when the selected set changes, but a data-refresh poll
 * (which only moves each car's latest GPS point) leaves it unchanged, so the map stays put. Compare with
 * `buildRoutesAutofitGeometrySignature`, which changes on any coordinate movement.
 */
export const buildRoutesAutofitSelectionSignature = (
  routes: IRideRoute[],
): string => {
  if (!routes?.length) {
    return "";
  }

  const ids = new Set<string>();

  routes.forEach(route => {
    if (!route) {
      return;
    }

    ids.add(
      String(route.rideId ?? route.stationRouteId ?? route.courseId ?? ""),
    );
  });

  if (!ids.size) {
    return "";
  }

  return buildPartsLayoutSignature(Array.from(ids), { sort: true });
};

/** Station coordinates only — used for map autofit (avoids polyline decode). */
export const getAutofitBoundMapFromRoutes = (
  routes: IRideRoute[],
): TLatLngTuple[] => {
  if (!routes?.length) {
    return [];
  }

  const seen = new Set<string>();
  const boundMap: TLatLngTuple[] = [];

  routes.forEach(route => {
    if (isTrackingRoute(route)) {
      const renderState = route.trackingRenderState;

      if (renderState?.pointCount) {
        const lastIndex = renderState.pointCount - 1;
        const [latitude, longitude] = getLatLngFromRenderState(
          renderState,
          lastIndex,
        );
        const key = coordinateDedupKey(latitude, longitude);

        if (!seen.has(key)) {
          seen.add(key);
          boundMap.push([latitude, longitude]);
        }

        return;
      }

      const lastLocation = route.locations?.at(-1);

      if (lastLocation && isLocationInfoWithValidMapCoordinates(lastLocation)) {
        const { latitude, longitude } = lastLocation;
        const key = coordinateDedupKey(latitude, longitude);

        if (!seen.has(key)) {
          seen.add(key);
          boundMap.push([latitude, longitude]);
        }
      }

      return;
    }

    if (!route?.locations?.length) {
      return;
    }

    route.locations.forEach(locationInfo => {
      if (!isLocationInfoWithValidMapCoordinates(locationInfo)) {
        return;
      }

      const { latitude, longitude } = locationInfo;
      const key = coordinateDedupKey(latitude, longitude);

      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      boundMap.push([latitude, longitude]);
    });
  });

  return boundMap.filter(isValidCoordinateTuple);
};

export const getBoundMapFromRoute = getAutofitBoundMapFromRoutes;

export const getBoundMapFromLocations = (
  locations: ILocationInfo[],
): TLatLngExpression[] => {
  if (!locations || !Array.isArray(locations)) {
    return [];
  }

  const boundMap = [] as TLatLngTuple[];
  locations.forEach(locationInfo => {
    if (!isLocationInfoWithValidMapCoordinates(locationInfo)) {
      return;
    }

    const { latitude, longitude } = locationInfo;

    if (!mapBoundsExistsLocationInfo({ bounds: boundMap, locationInfo })) {
      boundMap.push([latitude, longitude]);
    }
  });

  const finalBoundMap = boundMap.filter(isValidCoordinateTuple);

  return finalBoundMap;
};

export const normalizeGoogleTimestamp = (googleTimestamp: string): string =>
  googleTimestamp.padEnd(MAP_GOOGLE_TIMESTAMP_MS_LENGTH, "0");
